/**
 * Sprite loader for the real pixel-art assets in `public/sprites/` (generated from
 * `art/incoming/` — see art/ASSET_PROMPTS.md). Loads the PNGs as decoded <img> elements the
 * renderer composites crisp (nearest-neighbor). Everything is OPTIONAL: if there's no DOM, or a
 * file fails to load, `loadSprites()` resolves to `null` and the renderer falls back to its
 * procedural drawing — so the headless render harness and the offline-first guarantees still hold.
 */

/** The loaded image set the renderer draws (terrain tiles + characters + props). */
export interface SpriteSet {
  readonly grass: HTMLImageElement;
  readonly fairway: HTMLImageElement;
  readonly green: HTMLImageElement;
  readonly water: HTMLImageElement;
  readonly sand: HTMLImageElement;
  readonly golferBlue: HTMLImageElement;
  readonly golferRed: HTMLImageElement;
  readonly trees: readonly HTMLImageElement[]; // tree_1..3 variants
  readonly crowd: readonly HTMLImageElement[]; // crowd_1..8 spectators
  readonly flagGold: HTMLImageElement; // your pin
  readonly flagRed: HTMLImageElement; // rival pin
  readonly ball: HTMLImageElement;
  readonly gopher: HTMLImageElement;
  readonly teePad: HTMLImageElement;
}

/** All sprite filenames in `public/sprites/` (without extension). */
const FILES = [
  'grass_rough', 'fairway', 'green', 'water', 'sand',
  'golfer_blue', 'golfer_red',
  'tree_1', 'tree_2', 'tree_3',
  'crowd_1', 'crowd_2', 'crowd_3', 'crowd_4', 'crowd_5', 'crowd_6', 'crowd_7', 'crowd_8',
  'flag_gold', 'flag_red', 'ball', 'gopher', 'tee_pad',
] as const;

/** Load + decode all sprites. Resolves `null` (→ procedural fallback) with no DOM or on any error. */
export async function loadSprites(base = '/sprites/'): Promise<SpriteSet | null> {
  if (typeof Image === 'undefined') return null; // headless (render harness / tests)
  try {
    const map = new Map<string, HTMLImageElement>();
    await Promise.all(
      FILES.map(async (f) => {
        const img = new Image();
        img.src = `${base}${f}.png`;
        await img.decode();
        map.set(f, img);
      }),
    );
    const g = (k: string): HTMLImageElement => {
      const img = map.get(k);
      if (!img) throw new Error(`sprite ${k} missing`);
      return img;
    };
    return {
      grass: g('grass_rough'),
      fairway: g('fairway'),
      green: g('green'),
      water: g('water'),
      sand: g('sand'),
      golferBlue: g('golfer_blue'),
      golferRed: g('golfer_red'),
      trees: [g('tree_1'), g('tree_2'), g('tree_3')],
      crowd: [g('crowd_1'), g('crowd_2'), g('crowd_3'), g('crowd_4'), g('crowd_5'), g('crowd_6'), g('crowd_7'), g('crowd_8')],
      flagGold: g('flag_gold'),
      flagRed: g('flag_red'),
      ball: g('ball'),
      gopher: g('gopher'),
      teePad: g('tee_pad'),
    };
  } catch {
    return null; // a missing/failed asset → fall back to procedural rendering
  }
}
